import * as XLSX from "xlsx";
import dayjs from "dayjs";

export function exportContactsToXLSX(contacts: Contact[], customFileName?: string) {
  const headers = [
    "Household ID",
    "Name",
    "Phone Number",
    "Agent Email",
    "Added",
    "Age",
    "Overdue Balance",
  ];

  const formattedData =
    contacts.length > 0
      ? contacts.map((c) => ({
          "Household ID": c.household_number,
          Name:
            c?.first_name && c?.last_name
              ? `${c?.first_name?.toLowerCase()} ${c?.last_name?.toLowerCase()}`
              : "Name Not Found",
          "Phone Number": c.phone,
          "Agent Email": c.agent_email || "",
          Age: getAgeByDate(c.assignment_start),
          "Overdue Balance": c.overdue_balance,
          Added: dayjs(c.assignment_created_on).format("MM/DD/YYYY h:mmA"),
          End: c.assignment_end
            ? dayjs(c.assignment_end).format("MM/DD/YYYY h:mmA")
            : "Active",
        }))
      : [Object.fromEntries(headers.map((header) => [header, ""]))]; // empty row with headers

  const worksheet = XLSX.utils.json_to_sheet(formattedData, {
    skipHeader: false,
  });

  worksheet["!cols"] = [
    { wch: 15 },
    { wch: 25 },
    { wch: 20 },
    { wch: 40 },
    { wch: 12 },
    { wch: 15 },
    { wch: 20 },
    { wch: 20 },
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Contacts");

  const timestamp = dayjs().format("YYYY-MM-DD");
  const listName = contacts[0]?.list_name || "template";
  const filename = customFileName || `contacts_export_${listName}_${timestamp}.xlsx`;

  XLSX.writeFile(workbook, filename);
}

const exportFromFile = (contacts: any[], customFileName?: string) => {

};

export function getAgeByDate(dateStr: string): string {
  const inputDate = dayjs(dateStr);
  const today = dayjs();
  return `${today.diff(inputDate, 'day')} day(s)`;
}

const formatCurrencyV2 = (value: number | string) => {
  if(value === "") return;

  // Check if the input is a valid number
  if (typeof value !== "number" && isNaN(Number(value))) {
    return undefined; // Return undefined if input is not a valid number
  }

  // Convert the input value to a number if it's a string
  const numericValue = typeof value === "string" ? parseFloat(value) : value;

  // Format the number as currency
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(numericValue);

  return formatted || "";
};

export function dedupeContactsById<T extends { id: string | number }>(
  contacts: T[],
  {
    keep = "first", // or "last"
    logDuplicates = false,
  }: {
    keep?: "first" | "last";
    logDuplicates?: boolean;
  } = {}
): T[] {
  const map: Record<string | number, T> = {};
  const seen: Record<string | number, boolean> = {};
  const duplicates: T[] = [];

  for (const contact of contacts) {
    const contactId = contact.id;
    const alreadySeen = seen[contactId];

    if (alreadySeen) {
      if (keep === "last") {
        map[contactId] = contact; // overwrite previous
      }
      if (logDuplicates) duplicates.push(contact);
    } else {
      map[contactId] = contact;
      seen[contactId] = true;
    }
  }

  if (logDuplicates && duplicates.length) {
    const dupIdSet: Record<string, boolean> = {};
    const dupIds: (string | number)[] = [];

    for (const d of duplicates) {
      const key = String(d.id);
      if (!dupIdSet[key]) {
        dupIds.push(d.id);
        dupIdSet[key] = true;
      }
    }

    console.warn("Duplicate contact IDs filtered out:", dupIds);
  }

  return Object.values(map);
}


export {formatCurrencyV2};
